import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../components/layout/Layout';
import MainLayout from '../../components/layout/MainLayout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import {
  CodeFormFigure,
  CodeReadyFigure,
  EmbedFigure,
  MakeCodeShareFigure,
  MenuFigure,
  MyGamesFigure,
  RegisterCodeFigure,
  RegisterFieldsFigure,
} from './RegisterGuideFigures';

const linkStyle: React.CSSProperties = {
  color: '#FF6F0F',
  fontWeight: 700,
  textDecoration: 'underline',
};

const RegisterGuide: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();

  const tabs = [
    { id: 'register-new', label: '게임 등록', path: '/makecodearcade/register' },
    { id: 'my-games', label: '내가 등록한 게임', path: '/makecodearcade/register/my-games' },
    { id: 'register-guide', label: '등록 가이드', path: '/makecodearcade/register-guide' },
  ];

  const chapters = [
    { id: 'teacher-menu', label: '1. 코드 메뉴' },
    { id: 'teacher-code', label: '2. 코드 만들기' },
    { id: 'makecode', label: '3. 게임 공유' },
    { id: 'embed', label: '4. 주소와 임베드' },
    { id: 'upload', label: '5. 마루에 올리기' },
    { id: 'edit', label: '6. 올린 뒤 수정' },
  ];

  return (
    <MainLayout>
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
        <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', color: '#FF6F0F' }}>
          마루 아케이드 가이드
        </p>
        <h1 style={{
          margin: 0,
          maxWidth: 720,
          fontSize: isMobile ? 28 : 40,
          fontWeight: 800,
          lineHeight: 1.25,
          letterSpacing: '-0.03em',
          color: '#F4F4F8',
          fontFamily: 'var(--font-family-primary)',
        }}>
          회원가입 없이 게임을 올리는 방법
        </h1>
        <p style={{ margin: '12px 0 0', maxWidth: 680, fontSize: 16, lineHeight: 1.7, color: '#C8C8D4' }}>
          선생님이 만든 등록 코드를 학생에게 알려 주면, 학생은 계정을 만들지 않고 자신의 메이크코드 게임을 마루에 올릴 수 있습니다. 이 코드로 올린 게임은 평가용 게임이 아닙니다.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
          gap: 12,
          marginTop: 28,
        }}>
          {[
            { href: '#teacher-menu', step: '01', title: '선생님이 코드를 만들어요', text: '수업 관리에서 소속을 적고 등록 코드를 복사합니다.' },
            { href: '#makecode', step: '02', title: '학생이 게임을 공유해요', text: '메이크코드에서 게시한 뒤 주소와 임베드 코드를 복사합니다.' },
            { href: '#upload', step: '03', title: '마루에 붙여 넣어요', text: '등록 코드를 확인하고 게임 정보를 입력한 뒤 등록합니다.' },
          ].map((item) => (
            <a
              key={item.href}
              href={item.href}
              style={{
                display: 'block',
                textDecoration: 'none',
                background: '#1C1C28',
                border: '1px solid #2E2E3C',
                borderRadius: 16,
                padding: 18,
                color: '#F4F4F8',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: '#FF6F0F', marginBottom: 8 }}>{item.step}</div>
              <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>{item.title}</div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: '#C8C8D4' }}>{item.text}</div>
            </a>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, margin: '28px 0 16px', flexWrap: 'wrap' }}>
          {tabs.map((tab) => {
            const isActive = tab.id === 'register-guide';
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => navigate(tab.path)}
                style={{
                  padding: '10px 20px',
                  border: isActive ? '1.2px solid #FF6F0F' : '1.2px solid #2E2E3C',
                  borderRadius: 8,
                  backgroundColor: isActive ? '#3A2418' : '#1C1C28',
                  color: isActive ? '#FF6F0F' : '#F4F4F8',
                  fontSize: 14,
                  fontFamily: 'Pretendard',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <nav aria-label="가이드 목차" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
          {chapters.map((chapter) => (
            <a
              key={chapter.id}
              href={`#${chapter.id}`}
              style={{
                color: '#F4F4F8',
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 12px',
                borderRadius: 999,
                border: '1px solid #3A3A4C',
              }}
            >
              {chapter.label}
            </a>
          ))}
        </nav>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Chapter
            id="teacher-menu"
            step="01"
            title="선생님, 등록 코드 메뉴로 이동"
            figure={<MenuFigure />}
            isMobile={isMobile}
          >
            <p>
              교사 계정으로 로그인한 뒤 <Link to="/makecodearcade/upload-code" style={linkStyle}>등록 코드</Link>로 갑니다. 상단 메뉴에서는 수업 관리를 누르고 등록 코드를 고릅니다.
            </p>
            <p>
              바로 아래의 평가 코드 생성은 평가용 게임을 만들 때 쓰는 메뉴입니다. 학생이 회원가입 없이 게임을 올리게 하려면 등록 코드를 만듭니다.
            </p>
          </Chapter>

          <Chapter
            id="teacher-code"
            step="02"
            title="소속을 적고 코드를 학생에게 전달"
            figure={<><CodeFormFigure /><div style={{ height: 16 }} /><CodeReadyFigure /></>}
            isMobile={isMobile}
          >
            <p>
              소속은 필수입니다. 학교 이름이 미리 채워져 있어도 반 이름처럼 바꿔도 됩니다. 최대 40글자이고, 이 코드로 올린 게임 카드에는 여기 적은 소속이 표시됩니다.
            </p>
            <p>
              메모는 선택입니다. 학생에게 보이지 않고, 선생님이 코드를 구분할 때만 씁니다. 예: 게임 시간.
            </p>
            <p>
              코드 만들기를 누르면 영문 대문자와 숫자 8자리가 나옵니다. 복사를 눌러 학생에게 알려 줍니다. 한 코드를 여러 학생이 함께 써도 됩니다.
            </p>
            <p>
              소속을 바꾸려면 코드를 새로 만듭니다. 이미 올라간 게임의 소속은 그대로입니다. 코드를 삭제하면 그 코드로는 새 게임을 올릴 수 없고, 이미 올라간 게임은 남습니다.
            </p>
          </Chapter>

          <Chapter
            id="makecode"
            step="03"
            title="학생, 메이크코드에서 게임을 게시"
            figure={<MakeCodeShareFigure />}
            isMobile={isMobile}
          >
            <p>
              게임을 먼저 <a href="https://arcade.makecode.com" target="_blank" rel="noopener noreferrer" style={linkStyle}>메이크코드 아케이드</a>에서 만듭니다. 마루 홈의 게임 만들기 버튼으로도 같은 사이트가 열립니다.
            </p>
            <p>
              프로젝트를 연 뒤 화면 위쪽의 공유를 누릅니다. 공유 창에서 게시를 마쳐야 다른 사람이 플레이할 주소가 생깁니다. 아직 게시하지 않은 프로젝트는 마루에 올려도 게임이 열리지 않습니다.
            </p>
          </Chapter>

          <Chapter
            id="embed"
            step="04"
            title="게임 주소와 임베드 코드를 둘 다 복사"
            figure={<EmbedFigure />}
            isMobile={isMobile}
          >
            <p>
              게시가 끝나면 게임 주소가 나옵니다. 예: https://makecode.com/_MH2WAy9ieYgU 이 주소를 마루의 게임 URL 칸에 넣습니다.
            </p>
            <p>
              같은 공유 창에서 임베드를 엽니다. 나오는 HTML을 처음부터 끝까지 복사합니다. iframe이 들어 있는 그 전체가 시뮬레이터 코드입니다. 주소만 넣으면 마루 안의 플레이 화면이 비어 있습니다.
            </p>
          </Chapter>

          <Chapter
            id="upload"
            step="05"
            title="등록 코드를 확인하고 게임을 등록"
            figure={<><RegisterCodeFigure /><div style={{ height: 16 }} /><RegisterFieldsFigure /></>}
            isMobile={isMobile}
          >
            <p>
              <Link to="/makecodearcade/register" style={linkStyle}>게임 등록</Link>으로 갑니다. 학생 계정은 만들지 않아도 됩니다.
            </p>
            <p>
              선생님이 준 등록 코드를 입력하고 코드 확인을 누릅니다. 소속이 보이면 코드가 맞는 것입니다. 확인 전에는 아래의 게임 등록 버튼이 눌리지 않습니다.
            </p>
            <p>
              그다음 게임 제목, 게임 URL, 시뮬레이터 코드, 제작자 이름을 입력합니다. 이 네 칸은 필수입니다. 게임 설명, 소개 문구, 제작 과정 주소, 회사 또는 팀 이름, 학년, 반은 비워도 됩니다. 소개 문구를 적으면 게임 상세 페이지에 표시됩니다.
            </p>
            <p>
              게임 아이콘은 로그인한 뒤에만 올릴 수 있습니다. 코드만으로 등록하면 색 포스터에 제목 첫 글자가 대신 나옵니다.
            </p>
            <p>
              별점 없이 댓글만 받기를 켜면 별점 칸 없이 글만 받습니다. 로그인 없이 별점·리뷰 등록 허용을 켜면, 로그인하지 않은 사람도 그 브라우저에서 게임당 한 번 리뷰를 남길 수 있습니다.
            </p>
          </Chapter>

          <Chapter
            id="edit"
            step="06"
            title="올린 게임은 그 브라우저에서 수정"
            figure={<MyGamesFigure />}
            isMobile={isMobile}
          >
            <p>
              등록이 끝나면 <Link to="/makecodearcade/register/my-games" style={linkStyle}>내가 등록한 게임</Link>에 카드가 나타납니다. 수정은 게임을 올린 그 브라우저에서만 됩니다. 수정 키가 그 브라우저 안에만 저장되기 때문입니다.
            </p>
            <p>
              다른 컴퓨터, 다른 브라우저, 시크릿 창에서는 목록에 나오지 않고 수정도 되지 않습니다. 그때는 코드를 발급한 선생님이 등록 코드 페이지의 코드로 등록된 게임에서 고쳐 줍니다. 선생님이 고쳐도 등록한 사람, 소속, 코드는 바뀌지 않습니다.
            </p>
            <p>
              브라우저 저장공간을 지우거나, 수정 기능이 생기기 전에 올린 게임은 학생 쪽에서 고치지 못할 수 있습니다. 그때도 코드를 발급한 선생님이 수정합니다.
            </p>
          </Chapter>

          <section style={{
            background: '#1C1C28',
            border: '1px solid #2E2E3C',
            borderRadius: 20,
            padding: isMobile ? 18 : 24,
            color: '#F4F4F8',
          }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 800 }}>막히면 여기부터 확인</h2>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 15, lineHeight: 1.7, color: '#C8C8D4' }}>
              <li>평가 코드와 등록 코드는 다릅니다. 회원가입 없이 올릴 때는 등록 코드를 씁니다.</li>
              <li>게임 등록 버튼이 회색이면 코드 확인이 아직입니다. 소속이 보이는지 봅니다.</li>
              <li>게임 URL만 넣고 시뮬레이터 코드를 비우면 등록되지 않습니다. 임베드 HTML 전체가 필요합니다.</li>
              <li>다른 기기에서 수정이 안 되면 고장 난 것이 아닙니다. 올린 브라우저나 담당 선생님만 고칠 수 있습니다.</li>
            </ul>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18 }}>
              <Link to="/makecodearcade/upload-code" style={actionLinkStyle}>등록 코드 만들기</Link>
              <Link to="/makecodearcade/register" style={actionLinkStyle}>게임 등록</Link>
              <a href="https://arcade.makecode.com" target="_blank" rel="noopener noreferrer" style={actionLinkStyle}>게임 만들기</a>
            </div>
          </section>
        </div>
      </div>
    </MainLayout>
  );
};

const actionLinkStyle: React.CSSProperties = {
  display: 'inline-block',
  textDecoration: 'none',
  background: '#FF6F0F',
  color: '#FFFFFF',
  fontWeight: 800,
  fontSize: 14,
  borderRadius: 999,
  padding: '10px 16px',
};

const GuideCopy: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="arcade-guide-copy" style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 15, lineHeight: 1.75, color: '#3A3A3A' }}>
    <style>{`.arcade-guide-copy p { margin: 0; }`}</style>
    {children}
  </div>
);

const Chapter: React.FC<{
  id: string;
  step: string;
  title: string;
  figure: React.ReactNode;
  isMobile: boolean;
  children: React.ReactNode;
}> = ({ id, step, title, figure, isMobile, children }) => (
  <section
    id={id}
    style={{
      scrollMarginTop: 88,
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.05fr) minmax(280px, 0.95fr)',
      gap: isMobile ? 18 : 28,
      alignItems: 'start',
      background: '#FFFFFF',
      color: '#161616',
      borderRadius: 20,
      padding: isMobile ? 18 : 28,
    }}
  >
    <div>
      <div style={{ fontSize: 13, fontWeight: 800, color: '#FF6F0F', marginBottom: 8 }}>{step}</div>
      <h2 style={{ margin: '0 0 12px', fontSize: isMobile ? 22 : 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.35 }}>
        {title}
      </h2>
      {!isMobile && <GuideCopy>{children}</GuideCopy>}
    </div>
    <div>{figure}</div>
    {isMobile && <GuideCopy>{children}</GuideCopy>}
  </section>
);

export default RegisterGuide;
